export interface StateRegulation {
  slug: string;
  name: string;
  abbreviation: string;
  authorityName: string;
  permitName: string;
  permitLink: string;
  inspectionFrequency: string;
  rainTrigger: string;
  notes: string;
  lastReviewed?: string;
  complianceNuance?: {
      title: string;
      description: string;
      epaComparison: "Stricter than EPA" | "Same as EPA" | "Varies";
  };
  noiLink?: string; 
  agencyContact?: {
      phone?: string;
      email?: string;
      hotline?: string;
  };
}

export const stateRegulations: Record<string, StateRegulation> = {
  alabama: {
    slug: "alabama",
    name: "Alabama",
    abbreviation: "AL",
    authorityName: "Alabama Dept. of Environmental Management (ADEM)",
    permitName: "NPDES General Permit ALR100000",
    permitLink: "http://adem.alabama.gov/programs/water/construction.cnt",
    inspectionFrequency: "Monthly + within 24h of 0.75\" rain",
    rainTrigger: "0.75 inches",
    notes: "Alabama uses a higher rain trigger (0.75 inches) than the federal standard. Daily observations of the site entrance are also required.",
    complianceNuance: {
        title: "Higher Rain Threshold",
        description: "Inspections are triggered at 0.75 inches of rain, unlike the standard 0.50 inches found in most EPA-aligned states.",
        epaComparison: "Varies"
    },
    noiLink: "https://app.adem.alabama.gov/eNOI/",
    agencyContact: { phone: "334-271-7700", email: "stormwater@adem.alabama.gov" }
  },
  alaska: {
    slug: "alaska",
    name: "Alaska",
    abbreviation: "AK",
    authorityName: "Alaska Dept. of Environmental Conservation (DEC)",
    permitName: "APDES Construction General Permit (CGP)",
    permitLink: "https://dec.alaska.gov/water/wastewater/stormwater/construction",
    inspectionFrequency: "Every 14 days + 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Winter shutdowns require specific stabilization. A 'Spring Thaw' inspection is mandatory before resuming activity.",
    complianceNuance: {
        title: "Spring Thaw Protocol",
        description: "Special inspection schedules apply during winter. Sites must undergo a specific 'Spring Thaw' inspection.",
        epaComparison: "Stricter than EPA"
    },
    noiLink: "http://dec.alaska.gov/water/oasys/index.html",
    agencyContact: { phone: "907-269-6285" }
  },
  arizona: {
    slug: "arizona",
    name: "Arizona",
    abbreviation: "AZ",
    authorityName: "Arizona Dept. of Environmental Quality (ADEQ)",
    permitName: "AZPDES Construction General Permit (CGP)",
    permitLink: "http://www.azdeq.gov/node/524",
    inspectionFrequency: "Every 7 days OR 14 days + 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Arid climate waivers may apply. ADEQ focuses heavily on dust control and tracking in the dry season.",
    complianceNuance: {
        title: "myDEQ Portal",
        description: "All NOIs and NOTs must be filed electronically through the myDEQ portal. Paper submissions are rarely accepted.",
        epaComparison: "Same as EPA"
    },
    noiLink: "https://my.azdeq.gov/",
    agencyContact: { phone: "602-771-2300" }
  },
  arkansas: {
    slug: "arkansas",
    name: "Arkansas",
    abbreviation: "AR",
    authorityName: "Arkansas Dept. of Energy and Environment (DEQ)",
    permitName: "NPDES General Permit ARR150000",
    permitLink: "https://www.adeq.state.ar.us/water/permits/npdes/stormwater/",
    inspectionFrequency: "Every 7 days OR 14 days + 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Rain trigger is strictly 0.25 inches. A mailbox containing the SWPPP is required on-site if no trailer exists.",
    complianceNuance: {
        title: "0.25 Inch Trigger",
        description: "Inspections are triggered at 0.25 inches of rain, stricter than the federal 0.50 inch baseline.",
        epaComparison: "Stricter than EPA"
    },
    noiLink: "https://eportal.adeq.state.ar.us/",
    agencyContact: { phone: "501-682-0623" }
  },
  california: {
    slug: "california",
    name: "California",
    abbreviation: "CA",
    authorityName: "State Water Resources Control Board (SWRCB)",
    permitName: "Construction General Permit (2022 CGP)",
    permitLink: "https://www.waterboards.ca.gov/water_issues/programs/stormwater/construction.html",
    inspectionFrequency: "Weekly + pre-, during-, and post-QPE",
    rainTrigger: "QPE forecast: ≥50% chance and ≥0.5\" in 24h",
    notes: "All risk levels have weekly and qualifying precipitation event (QPE) visual inspections. The pre-QPE inspection is generally due within 72 hours before the event, during-QPE inspections every 24 hours, and a post-QPE inspection within 96 hours when actual accumulation reaches 0.5 inch or more. Confirm project-specific TMDL and monitoring requirements in SMARTS.",
    lastReviewed: "2026-09-12",
    complianceNuance: {
        title: "Forecast-Based QPE Inspections",
        description: "California's QPE definition starts with both a probability-of-precipitation and quantitative-forecast threshold, then applies separate pre-, during-, and post-event inspection windows.",
        epaComparison: "Varies"
    },
    noiLink: "https://smarts.waterboards.ca.gov/",
    agencyContact: { email: "stormwater@waterboards.ca.gov", phone: "1-866-563-3107" }
  },
  colorado: {
    slug: "colorado",
    name: "Colorado",
    abbreviation: "CO",
    authorityName: "Colorado Dept. of Public Health & Environment (CDPHE)",
    permitName: "COR400000 Stormwater Discharge Permit",
    permitLink: "https://cdphe.colorado.gov/wq-construction-general-permit",
    inspectionFrequency: "Every 14 days + post-storm",
    rainTrigger: "Precipitation causing runoff",
    notes: "Inspections required after any event causing surface runoff. Special attention to snowmelt runoff in spring.",
    complianceNuance: {
        title: "Runoff-Based Trigger",
        description: "Colorado uses a performance-based trigger ('causing runoff') rather than a strict numerical rain value for some sites.",
        epaComparison: "Varies"
    },
    noiLink: "https://cdphe.colorado.gov/wq-construction-general-permit",
    agencyContact: { phone: "303-692-3500", email: "cdphe_wqcd_permits@state.co.us" }
  },
  connecticut: {
    slug: "connecticut",
    name: "Connecticut",
    abbreviation: "CT",
    authorityName: "Connecticut DEEP",
    permitName: "General Permit for Discharge of Stormwater",
    permitLink: "https://portal.ct.gov/DEEP/Water-Regulating-and-Discharges/Stormwater/Construction-Stormwater-GP",
    inspectionFrequency: "Weekly + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Turbidity monitoring required for larger sites. A Plan Qualified Professional (PQP) must certify the SWPPP.",
    complianceNuance: {
        title: "Plan Qualified Professional",
        description: "A PQP (PE, Landscape Architect, or CPESC) must certify the initial SWPPP design.",
        epaComparison: "Stricter than EPA"
    },
    noiLink: "https://filings.deep.ct.gov/DEEPPortal/Master/Home",
    agencyContact: { phone: "860-424-3000" }
  },
  delaware: {
    slug: "delaware",
    name: "Delaware",
    abbreviation: "DE",
    authorityName: "Delaware DNREC",
    permitName: "NPDES Construction General Permit",
    permitLink: "https://dnrec.alpha.delaware.gov/watershed-stewardship/sediment-stormwater/construction/",
    inspectionFrequency: "Weekly + within 24h of rain",
    rainTrigger: "0.5 inches",
    notes: "Certified Construction Reviewer (CCR) required for sites > 50 acres. Weekly written reports mandatory.",
    noiLink: "https://dnrec.alpha.delaware.gov/watershed-stewardship/sediment-stormwater/enoi/",
    agencyContact: { phone: "302-739-9921" }
  },
  florida: {
    slug: "florida",
    name: "Florida",
    abbreviation: "FL",
    authorityName: "Florida Department of Environmental Protection (FDEP)",
    permitName: "NPDES Generic Permit for Stormwater Discharge",
    permitLink: "https://floridadep.gov/water/stormwater/content/construction-activity",
    inspectionFrequency: "Weekly + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "FDEP's construction program generally covers projects disturbing one or more acres, including smaller sites within a larger common plan of development. The SWPPP template calls for qualified personnel to inspect weekly and within 24 hours after a storm producing 0.50 inch or more.",
    lastReviewed: "2026-09-12",
    complianceNuance: {
        title: "Coverage Includes Common Plans",
        description: "A site below one acre can still need coverage when it is part of a larger common plan that will disturb at least one acre. Use FDEP's current permit materials and SWPPP template to determine the applicable duties.",
        epaComparison: "Varies"
    },
    noiLink: "http://www.fldepportal.com/go/",
    agencyContact: { phone: "850-245-8336", email: "NPDES-stormwater@floridadep.gov" }
  },
  georgia: {
    slug: "georgia",
    name: "Georgia",
    abbreviation: "GA",
    authorityName: "Georgia EPD",
    permitName: "NPDES General Permit No. GAR100000",
    permitLink: "https://epd.georgia.gov/watershed-protection-branch/storm-water",
    inspectionFrequency: "Daily (Rain Log) + Weekly (BMPs)",
    rainTrigger: "0.5 inches",
    notes: "Georgia requires daily rainfall logs even if no inspection occurs. 'Triple Sampling' protocol applies to impaired waters.",
    complianceNuance: {
        title: "Daily Rain Logging",
        description: "You must log rainfall data every single day, separate from the weekly inspection report.",
        epaComparison: "Stricter than EPA"
    },
    noiLink: "https://geos.epd.georgia.gov/GA/GEOS/Public/GovEnt/Shared/Pages/Main/Login.aspx",
    agencyContact: { phone: "404-463-1511" }
  },
  hawaii: {
    slug: "hawaii",
    name: "Hawaii",
    abbreviation: "HI",
    authorityName: "Hawaii Dept. of Health (DOH)",
    permitName: "NPDES General Permit (Appendix C)",
    permitLink: "https://health.hawaii.gov/cwb/permitting/npdes-general-permits/",
    inspectionFrequency: "Weekly + within 24h of rain",
    rainTrigger: "0.25 inches",
    notes: "Island ecosystem protection is paramount. Immediate cessation of discharge if turbidity plume is visible.",
    noiLink: "https://eha-cloud.doh.hawaii.gov/epermit/",
    agencyContact: { phone: "808-586-4309" }
  },
  idaho: {
    slug: "idaho",
    name: "Idaho",
    abbreviation: "ID",
    authorityName: "Idaho Dept. of Environmental Quality (DEQ)",
    permitName: "Idaho Construction General Permit (CGP)",
    permitLink: "https://www.deq.idaho.gov/permits/water-quality-permits-certifications/storm-water-permits/",
    inspectionFrequency: "Every 7 days OR every 14 days and within 24h of a qualifying storm",
    rainTrigger: "0.25 inches (or snowmelt)",
    notes: "Idaho DEQ administers permit IDR100000. The current permit term runs through February 9, 2027; daily inspections apply while dewatering discharges occur.",
    lastReviewed: "2026-09-12",
    noiLink: "https://www2.deq.idaho.gov/water/ipdes",
    agencyContact: { phone: "208-373-0502" }
  },
  illinois: {
    slug: "illinois",
    name: "Illinois",
    abbreviation: "IL",
    authorityName: "Illinois EPA",
    permitName: "General NPDES Permit ILR10",
    permitLink: "https://epa.illinois.gov/topics/forms/water-permits/storm-water/construction.html",
    inspectionFrequency: "Every 7 days + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Requires 'Incident of Non-Compliance' (ION) submission within 5 days if a violation is found.",
    lastReviewed: "2026-09-12",
    noiLink: "https://epa.illinois.gov/topics/forms/water-permits/storm-water/construction.html",
    agencyContact: { phone: "217-782-0610" }
  },
  indiana: {
    slug: "indiana",
    name: "Indiana",
    abbreviation: "IN",
    authorityName: "Indiana DEM (IDEM)",
    permitName: "Construction Stormwater General Permit (CSGP)",
    permitLink: "https://www.in.gov/idem/stormwater/construction-stormwater/",
    inspectionFrequency: "Weekly + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Transitioned from Rule 5 to CSGP. Self-monitoring reports must be kept on site for 3 years.",
    noiLink: "https://www.in.gov/idem/stormwater/construction-stormwater/notice-of-intent-noi/",
    agencyContact: { phone: "317-232-8603" }
  },
  iowa: {
    slug: "iowa",
    name: "Iowa",
    abbreviation: "IA",
    authorityName: "Iowa DNR",
    permitName: "NPDES General Permit No. 2",
    permitLink: "https://www.iowadnr.gov/Environmental-Protection/Water-Quality/NPDES-Storm-Water/Construction-Permits",
    inspectionFrequency: "Every 7 days",
    rainTrigger: "0.5 inches",
    notes: "Topsoil preservation is a focus. Ensure soil stockpiles are stabilized.",
    noiLink: "https://programs.iowadnr.gov/stormwater/pages/home.aspx",
    agencyContact: { phone: "515-725-8200" }
  },
  kansas: {
    slug: "kansas",
    name: "Kansas",
    abbreviation: "KS",
    authorityName: "Kansas Dept. of Health and Environment (KDHE)",
    permitName: "General Permit for Construction Stormwater",
    permitLink: "https://www.kdhe.ks.gov/757/Construction-Stormwater",
    inspectionFrequency: "Every 14 days + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "14-day routine inspection schedule is commonly used here.",
    noiLink: "https://www.kdhe.ks.gov/757/Construction-Stormwater",
    agencyContact: { phone: "785-296-5545" }
  },
  kentucky: {
    slug: "kentucky",
    name: "Kentucky",
    abbreviation: "KY",
    authorityName: "Kentucky Division of Water (DOW)",
    permitName: "KYR10 General Permit",
    permitLink: "https://eec.ky.gov/Environmental-Protection/Water/PermitCert/KPDES/Pages/Stormwater.aspx",
    inspectionFrequency: "Every 7 days OR 14 days + rain",
    rainTrigger: "0.5 inches",
    notes: "Karst terrain protection (sinkholes) is a critical compliance focus in KY.",
    noiLink: "https://dep.gateway.ky.gov/eportal/",
    agencyContact: { phone: "502-564-3410" }
  },
  louisiana: {
    slug: "louisiana",
    name: "Louisiana",
    abbreviation: "LA",
    authorityName: "Louisiana DEQ (LDEQ)",
    permitName: "LPDES General Permit LAR100000",
    permitLink: "https://www.deq.louisiana.gov/page/lpdes-water-permits",
    inspectionFrequency: "Weekly + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "High water table requires specific dewatering BMPs. Sediment control is strictly enforced near wetlands.",
    noiLink: "https://www.deq.louisiana.gov/",
    agencyContact: { phone: "225-219-3181" }
  },
  maine: {
    slug: "maine",
    name: "Maine",
    abbreviation: "ME",
    authorityName: "Maine DEP",
    permitName: "Maine Construction General Permit (MCGP)",
    permitLink: "https://www.maine.gov/dep/land/stormwater/construction.html",
    inspectionFrequency: "Weekly + within 24h of storm",
    rainTrigger: "0.25 inches",
    notes: "Winter construction rules apply Nov 1 - Apr 15. Sites must be buttoned up for snow.",
    complianceNuance: {
        title: "Winter Stabilization",
        description: "Areas not worked for >3 days must be stabilized during winter months (vs 14 days normally).",
        epaComparison: "Stricter than EPA"
    },
    noiLink: "https://www.maine.gov/dep/land/stormwater/construction.html",
    agencyContact: { phone: "207-287-7688" }
  },
  maryland: {
    slug: "maryland",
    name: "Maryland",
    abbreviation: "MD",
    authorityName: "Maryland Dept. of the Environment (MDE)",
    permitName: "General Permit for Construction (20-CP)",
    permitLink: "https://mde.maryland.gov/programs/water/wwp/Documents/20CP-Final/20CP-Permit-Final.pdf",
    inspectionFrequency: "Every 4 business days OR weekly + post-rain",
    rainTrigger: "0.25 inches or more in 24h",
    notes: "The 20-CP permit allows inspections at least once every four business days, or once every seven calendar days plus within 24 hours after a storm producing 0.25 inch or more in 24 hours. MDE may require more frequent inspections for discharges to sensitive waters.",
    lastReviewed: "2026-09-12",
    complianceNuance: {
        title: "Two Inspection Schedule Options",
        description: "The weekly option carries an event-driven inspection requirement; the four-business-day option does not use that same post-rain cadence. Project and receiving-water conditions can add requirements.",
        epaComparison: "Varies"
    },
    noiLink: "https://egov.maryland.gov/mde/generalpermits/",
    agencyContact: { phone: "410-537-3000" }
  },
  massachusetts: {
    slug: "massachusetts",
    name: "Massachusetts",
    abbreviation: "MA",
    authorityName: "US EPA Region 1 (Direct Implementation)",
    permitName: "EPA Construction General Permit (CGP)",
    permitLink: "https://www.epa.gov/npdes/2022-construction-general-permit-cgp",
    inspectionFrequency: "Every 7 days OR 14 days + 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Direct EPA implementation. No state-level general permit; use federal system.",
    complianceNuance: {
        title: "Direct EPA Oversight",
        description: "MA is one of the few states where EPA Region 1 directly issues permits. You must use the federal eNOI system.",
        epaComparison: "Same as EPA"
    },
    noiLink: "https://cdx.epa.gov/",
    agencyContact: { phone: "617-918-1111" }
  },
  michigan: {
    slug: "michigan",
    name: "Michigan",
    abbreviation: "MI",
    authorityName: "Michigan EGLE",
    permitName: "Permit-by-Rule 323.2190",
    permitLink: "https://www.michigan.gov/egle/about/organization/water-resources/soil-erosion/construction-stormwater-overview",
    inspectionFrequency: "Weekly + within 24h of rain-caused discharge",
    rainTrigger: "A rain event that causes a site discharge",
    notes: "A certified storm water operator must inspect at least weekly and within 24 hours after a rain event that causes a site discharge. For one-to-five-acre sites, permit-by-rule coverage is automatic after the required Part 91 soil-erosion coverage; sites disturbing five acres or more must also submit a Notice of Coverage through MiEnviro.",
    lastReviewed: "2026-09-12",
    complianceNuance: {
        title: "Permit-by-Rule and Certified Operator",
        description: "Coverage steps differ at the five-acre threshold, while inspection duties are assigned to a Michigan-certified storm water operator.",
        epaComparison: "Varies"
    },
    noiLink: "https://mienviro.michigan.gov/",
    agencyContact: { phone: "800-662-9278" }
  },
  minnesota: {
    slug: "minnesota",
    name: "Minnesota",
    abbreviation: "MN",
    authorityName: "Minnesota Pollution Control Agency (MPCA)",
    permitName: "MN General Stormwater Permit (CSW)",
    permitLink: "https://www.pca.state.mn.us/business-with-us/construction-stormwater",
    inspectionFrequency: "Every 7 days + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Infiltration basins must be protected from sediment to prevent clogging.",
    noiLink: "https://rsp.pca.state.mn.us/",
    agencyContact: { phone: "651-296-6300" }
  },
  mississippi: {
    slug: "mississippi",
    name: "Mississippi",
    abbreviation: "MS",
    authorityName: "Mississippi DEQ (MDEQ)",
    permitName: "Large Construction General Permit",
    permitLink: "https://www.mdeq.ms.gov/permits/environmental-permits-division/general-permits/",
    inspectionFrequency: "Weekly + within 24h of rain",
    rainTrigger: "0.5 inches",
    notes: "Distinguishes between Small (1-5 acres) and Large (>5 acres) projects.",
    noiLink: "https://www.mdeq.ms.gov/ensite/",
    agencyContact: { phone: "601-961-5171" }
  },
  missouri: {
    slug: "missouri",
    name: "Missouri",
    abbreviation: "MO",
    authorityName: "Missouri DNR",
    permitName: "MO-R100000 General Permit",
    permitLink: "https://dnr.mo.gov/water/business-industry-other-entities/permits-certification-engineering-fees/stormwater/construction",
    inspectionFrequency: "Every 7 days OR 14 days + runoff event",
    rainTrigger: "Runoff Event",
    notes: "Focus on Karst topography protection in southern MO.",
    noiLink: "https://dnr.mo.gov/moguide/",
    agencyContact: { phone: "573-751-1300" }
  },
  montana: {
    slug: "montana",
    name: "Montana",
    abbreviation: "MT",
    authorityName: "Montana DEQ",
    permitName: "General Permit for Storm Water (MTR100000)",
    permitLink: "https://deq.mt.gov/water/assistance",
    inspectionFrequency: "Every 7 days + 24h after 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Strict 0.25 inch trigger. Winter shutdowns allowed but must be documented.",
    noiLink: "https://deq.mt.gov/Portal/",
    agencyContact: { phone: "406-444-2544" }
  },
  nebraska: {
    slug: "nebraska",
    name: "Nebraska",
    abbreviation: "NE",
    authorityName: "Nebraska DEE",
    permitName: "NPDES General Permit CSW-2021",
    permitLink: "https://dee.nebraska.gov/sites/default/files/publications/NER210000%20CSW%20General%20Permit%20-%20Revised.pdf",
    inspectionFrequency: "Every 14 days + within 24h of 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Use the current Nebraska construction stormwater permit and project SWPPP to determine the applicable inspection schedule and any approved reduced-frequency conditions.",
    lastReviewed: "2026-09-12",
    noiLink: "https://dee.nebraska.gov/",
    agencyContact: { phone: "402-471-2186" }
  },
  nevada: {
    slug: "nevada",
    name: "Nevada",
    abbreviation: "NV",
    authorityName: "Nevada NDEP",
    permitName: "Stormwater General Permit NVR100000",
    permitLink: "https://ndep.nv.gov/water/water-pollution-control/permitting/stormwater-discharge/construction",
    inspectionFrequency: "Every 7 days OR 14 days + 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Arid climate. Flash flood risks require robust perimeter controls.",
    noiLink: "https://ndep.nv.gov/water/water-pollution-control/permitting/stormwater-discharge/construction",
    agencyContact: { phone: "775-687-9400" }
  },
  "new-hampshire": {
    slug: "new-hampshire",
    name: "New Hampshire",
    abbreviation: "NH",
    authorityName: "US EPA Region 1 (Direct)",
    permitName: "EPA Construction General Permit (CGP)",
    permitLink: "https://www.epa.gov/npdes/2022-construction-general-permit-cgp",
    inspectionFrequency: "Weekly OR 14-day + 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Non-authorized state. EPA enforces directly using federal eNOI.",
    complianceNuance: { title: "EPA Direct", description: "NH is a non-authorized state. EPA enforces directly.", epaComparison: "Same as EPA" },
    noiLink: "https://cdx.epa.gov/",
    agencyContact: { phone: "617-918-1111" }
  },
  "new-jersey": {
    slug: "new-jersey",
    name: "New Jersey",
    abbreviation: "NJ",
    authorityName: "NJ DEP",
    permitName: "RFA Construction General Permit (5G3)",
    permitLink: "https://www.nj.gov/dep/dwq/5g3.htm",
    inspectionFrequency: "Weekly",
    rainTrigger: "Weekly Routine",
    notes: "Soil Conservation Districts (SCD) must certify plans before RFA filing.",
    complianceNuance: { title: "Soil Conservation District", description: "Plans must be certified by the local SCD before filing RFA with DEP.", epaComparison: "Stricter than EPA" },
    noiLink: "https://www.njdeponline.com/",
    agencyContact: { phone: "609-633-7021" }
  },
  "new-mexico": {
    slug: "new-mexico",
    name: "New Mexico",
    abbreviation: "NM",
    authorityName: "US EPA Region 6 (Direct)",
    permitName: "EPA Construction General Permit (CGP)",
    permitLink: "https://www.epa.gov/npdes/2022-construction-general-permit-cgp",
    inspectionFrequency: "Every 14 days + within 24h of 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Direct EPA implementation. Arid waivers often used.",
    lastReviewed: "2026-09-12",
    complianceNuance: { title: "EPA Direct", description: "NM is a non-authorized state. EPA enforces directly.", epaComparison: "Same as EPA" },
    noiLink: "https://cdx.epa.gov/",
    agencyContact: { phone: "214-665-2200" }
  },
  "new-york": {
    slug: "new-york",
    name: "New York",
    abbreviation: "NY",
    authorityName: "NYS Dept. of Environmental Conservation (DEC)",
    permitName: "SPDES General Permit GP-0-25-001",
    permitLink: "https://dec.ny.gov/environmental-protection/water/water-quality/stormwater/construction-activity-permit",
    inspectionFrequency: "Daily contractor checks + qualified inspection weekly",
    rainTrigger: "No universal post-rain trigger; follow permit schedule",
    notes: "GP-0-25-001 is effective January 29, 2025 through January 28, 2030. A trained contractor checks active work areas daily; a qualified inspector normally inspects every seven calendar days. Twice-per-seven-day inspections apply to certain sites authorized to disturb more than five acres and certain sensitive-water discharges.",
    lastReviewed: "2026-09-12",
    complianceNuance: {
        title: "Daily Checks Are Separate from Inspections",
        description: "The trained contractor's daily check of active work areas does not replace the qualified inspector's documented schedule. Confirm whether the twice-weekly or reduced inactive-site provisions apply.",
        epaComparison: "Varies"
    },
    noiLink: "https://dec.ny.gov/maps/nysdec-nform",
    agencyContact: { phone: "518-402-8109", email: "stormwater_info@dec.ny.gov" }
  },
  "north-carolina": {
    slug: "north-carolina",
    name: "North Carolina",
    abbreviation: "NC",
    authorityName: "NC DEQ",
    permitName: "NCG01 Construction General Permit",
    permitLink: "https://deq.nc.gov/about/divisions/energy-mineral-land-resources/stormwater/stormwater-program/construction-stormwater",
    inspectionFrequency: "Weekly + within 24h of 1.0\" rain",
    rainTrigger: "1.0 inches (Higher than federal)",
    notes: "NC uses a 1.0 inch rain trigger for some inspections, but weekly checks are standard. E&SC Plan must be approved locally.",
    noiLink: "https://deq.nc.gov/about/divisions/energy-mineral-land-resources/stormwater/stormwater-program/construction-stormwater/e-noi",
    agencyContact: { phone: "919-707-9100" }
  },
  "north-dakota": {
    slug: "north-dakota",
    name: "North Dakota",
    abbreviation: "ND",
    authorityName: "North Dakota DEQ",
    permitName: "NDPDES General Permit NDR10-0000",
    permitLink: "https://deq.nd.gov/WQ/2_NDPDES_Permits/7_Stormwater/Construction.aspx",
    inspectionFrequency: "Every 14 days + within 24h of 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Dewatering requires specific BMPs. Frozen ground inspections can be suspended.",
    noiLink: "https://deq.nd.gov/WQ/2_NDPDES_Permits/7_Stormwater/Construction.aspx",
    agencyContact: { phone: "701-328-5210" }
  },
  ohio: {
    slug: "ohio",
    name: "Ohio",
    abbreviation: "OH",
    authorityName: "Ohio EPA",
    permitName: "NPDES General Permit OHC000005",
    permitLink: "https://epa.ohio.gov/divisions-and-offices/surface-water/permitting/stormwater-discharges-from-small-and-large-construction-activities",
    inspectionFrequency: "Every 7 days",
    rainTrigger: "0.5 inches (if using 14 day sched)",
    notes: "Big Darby Creek watershed has stricter rules. Post-construction stormwater management is heavily regulated.",
    noiLink: "https://ebiz.epa.ohio.gov/",
    agencyContact: { phone: "614-644-2001" }
  },
  oklahoma: {
    slug: "oklahoma",
    name: "Oklahoma",
    abbreviation: "OK",
    authorityName: "Oklahoma DEQ",
    permitName: "OKR10 Construction General Permit",
    permitLink: "https://www.deq.ok.gov/water-quality-division/stormwater/construction-stormwater/",
    inspectionFrequency: "Every 14 days + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Requires a rain gauge on site. Daily rainfall must be recorded.",
    noiLink: "https://www.deq.ok.gov/water-quality-division/stormwater/construction-stormwater/",
    agencyContact: { phone: "405-702-8100" }
  },
  oregon: {
    slug: "oregon",
    name: "Oregon",
    abbreviation: "OR",
    authorityName: "Oregon DEQ",
    permitName: "NPDES 1200-C General Permit",
    permitLink: "https://www.oregon.gov/deq/wq/wqpermits/Pages/Stormwater-Construction.aspx",
    inspectionFrequency: "Daily (Visual) + Weekly (Formal)",
    rainTrigger: "Any runoff event",
    notes: "1200-C permit is very strict. Daily visual monitoring required when active. Turbidity monitoring required for some sites.",
    complianceNuance: { title: "Daily Visual Monitoring", description: "Oregon requires brief visual checks daily during active construction.", epaComparison: "Stricter than EPA" },
    noiLink: "https://yourdeqonline.oregon.gov/",
    agencyContact: { phone: "503-229-5696" }
  },
  pennsylvania: {
    slug: "pennsylvania",
    name: "Pennsylvania",
    abbreviation: "PA",
    authorityName: "Pennsylvania DEP",
    permitName: "PAG-02 General Permit",
    permitLink: "https://www.dep.pa.gov/Business/Water/CleanWater/StormwaterMgmt/Stormwater%20Construction/Pages/default.aspx",
    inspectionFrequency: "Weekly + after runoff events",
    rainTrigger: "Runoff Event",
    notes: "County Conservation Districts (CCD) manage most approvals. E&S Plan must be on site at all times.",
    noiLink: "https://www.dep.pa.gov/DataandTools/Pages/ePermitting.aspx",
    agencyContact: { phone: "717-787-2147" }
  },
  "rhode-island": {
    slug: "rhode-island",
    name: "Rhode Island",
    abbreviation: "RI",
    authorityName: "Rhode Island DEM",
    permitName: "RIPDES Construction General Permit",
    permitLink: "http://www.dem.ri.gov/programs/water/permits/ripdes/stormwater-construction.php",
    inspectionFrequency: "Weekly + within 24h of 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Coastal features require extra buffers.",
    noiLink: "http://www.dem.ri.gov/programs/water/permits/ripdes/stormwater-construction.php",
    agencyContact: { phone: "401-222-4700" }
  },
  "south-carolina": {
    slug: "south-carolina",
    name: "South Carolina",
    abbreviation: "SC",
    authorityName: "South Carolina DHEC",
    permitName: "NPDES General Permit SCR100000",
    permitLink: "https://scdhec.gov/environment/water-quality/stormwater/construction-activities",
    inspectionFrequency: "Every 7 days",
    rainTrigger: "0.5 inches",
    notes: "Coastal counties have different requirements. CEPSCI certified inspectors often required.",
    noiLink: "https://efile.scdhec.gov/",
    agencyContact: { phone: "803-898-3432" }
  },
  "south-dakota": {
    slug: "south-dakota",
    name: "South Dakota",
    abbreviation: "SD",
    authorityName: "South Dakota DANR",
    permitName: "General Permit SDR100000",
    permitLink: "https://danr.sd.gov/OfficeOfWater/SurfaceWaterQuality/stormwater/Construction.aspx",
    inspectionFrequency: "Weekly + within 24h of 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Standard EPA alignment.",
    noiLink: "https://danr.sd.gov/OfficeOfWater/SurfaceWaterQuality/stormwater/Construction.aspx",
    agencyContact: { phone: "605-773-3351" }
  },
  tennessee: {
    slug: "tennessee",
    name: "Tennessee",
    abbreviation: "TN",
    authorityName: "Tennessee TDEC",
    permitName: "TNR100000 Construction General Permit",
    permitLink: "https://www.tn.gov/environment/permit-permits/water-permits1/npdes-permits1/npdes-stormwater-permitting-program/npdes-stormwater-construction-permit.html",
    inspectionFrequency: "Twice Weekly (at least 72h apart)",
    rainTrigger: "N/A (Schedule based)",
    notes: "TN requires inspections TWICE a week (e.g. Tuesday/Friday). This is stricter than the standard weekly rule.",
    complianceNuance: { title: "Twice Weekly Inspections", description: "Inspections must be performed twice per week, at least 72 hours apart. Rain events do not trigger extra inspections if this schedule is met.", epaComparison: "Stricter than EPA" },
    noiLink: "https://tdec.tn.gov/cgp/",
    agencyContact: { phone: "1-888-891-8332" }
  },
  texas: {
    slug: "texas",
    name: "Texas",
    abbreviation: "TX",
    authorityName: "Texas Commission on Environmental Quality (TCEQ)",
    permitName: "TPDES General Permit TXR150000 (2023)",
    permitLink: "https://www.tceq.texas.gov/permitting/stormwater/construction",
    inspectionFrequency: "Every 7 days OR 14 days + 0.5\" Rain",
    rainTrigger: "0.5 inches",
    notes: "Primary operators submit NOIs via STEERS. Unique 'Construction Site Notice' (CSN) rule allows small sites (1-5 acres) to operate without submitting an NOI to Austin.",
    complianceNuance: {
        title: "Construction Site Notice (CSN)",
        description: "Texas allows small sites (1-5 acres) to post a CSN without submitting an NOI to TCEQ, a flexibility not found in the standard EPA process.",
        epaComparison: "Varies"
    },
    noiLink: "https://www3.tceq.texas.gov/steers/",
    agencyContact: { phone: "512-239-4671", email: "swgp@tceq.texas.gov" }
  },
  utah: {
    slug: "utah",
    name: "Utah",
    abbreviation: "UT",
    authorityName: "Utah DEQ",
    permitName: "UPDES Construction General Permit (CGP)",
    permitLink: "https://deq.utah.gov/water-quality/general-construction-storm-water-updes-permits",
    inspectionFrequency: "Weekly OR 14-days + 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Common plan used. DWQ provides a template SWPPP.",
    noiLink: "https://deq.utah.gov/water-quality/updes-ereporting",
    agencyContact: { phone: "801-536-4300" }
  },
  vermont: {
    slug: "vermont",
    name: "Vermont",
    abbreviation: "VT",
    authorityName: "Vermont DEC",
    permitName: "Construction General Permit 3-9020",
    permitLink: "https://dec.vermont.gov/watershed/stormwater/permit-information-applications-fees/construction-general-permit-3-9020",
    inspectionFrequency: "Weekly",
    rainTrigger: "Runoff Event",
    notes: "Risk-based system (Low vs Moderate). Moderate risk requires more robust plan.",
    noiLink: "https://anronline.vermont.gov/",
    agencyContact: { phone: "802-828-1115" }
  },
  virginia: {
    slug: "virginia",
    name: "Virginia",
    abbreviation: "VA",
    authorityName: "Virginia DEQ",
    permitName: "VPDES Construction General Permit (VAR10)",
    permitLink: "https://www.deq.virginia.gov/water/stormwater",
    inspectionFrequency: "Every 5 business days OR 10 days + 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Requires 'Responsible Land Disturber' (RLD) designation.",
    noiLink: "https://www.deq.virginia.gov/",
    agencyContact: { phone: "804-698-4000" }
  },
  washington: {
    slug: "washington",
    name: "Washington",
    abbreviation: "WA",
    authorityName: "Department of Ecology",
    permitName: "Construction Stormwater General Permit (CSWGP)",
    permitLink: "https://ecology.wa.gov/Regulations-Permits/Permits-certifications/Stormwater-general-permits/Construction-stormwater-permit",
    inspectionFrequency: "Weekly and within 24h of discharge",
    rainTrigger: "Any Discharge Event",
    notes: "The current permit is effective January 1, 2026 through December 31, 2030. A CESCL inspects at least once each calendar week and within 24 hours of any discharge. Turbidity or transparency monitoring applies to discharging sites under five acres; turbidity monitoring applies at five acres or more. Weekly pH monitoring is conditional, including certain significant concrete work or engineered-soil activities.",
    lastReviewed: "2026-09-12",
    complianceNuance: {
        title: "Discharge Sampling and CESCL Oversight",
        description: "Sampling method depends on disturbed acreage, while pH monitoring applies only under specified permit conditions. Consult the 2026 permit before assigning a universal sampling checklist.",
        epaComparison: "Varies"
    },
    noiLink: "https://ecology.wa.gov/regulations-permits/permits-certifications/stormwater-general-permits/construction-stormwater-permit/ecoverage-packet",
    agencyContact: { phone: "360-407-6000" }
  },
  "west-virginia": {
    slug: "west-virginia",
    name: "West Virginia",
    abbreviation: "WV",
    authorityName: "West Virginia DEP",
    permitName: "NPDES General Water Pollution Control Permit",
    permitLink: "https://dep.wv.gov/WWE/Programs/stormwater/csw/Pages/default.aspx",
    inspectionFrequency: "Every 7 days + 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Steep slope projects have additional BMP requirements.",
    noiLink: "https://apps.dep.wv.gov/ess/",
    agencyContact: { phone: "304-926-0499" }
  },
  wisconsin: {
    slug: "wisconsin",
    name: "Wisconsin",
    abbreviation: "WI",
    authorityName: "Wisconsin DNR",
    permitName: "WPDES Construction Site Storm Water Runoff General Permit",
    permitLink: "https://dnr.wisconsin.gov/topic/Stormwater/construction",
    inspectionFrequency: "Weekly + within 24h of 0.5\" rain",
    rainTrigger: "0.5 inches",
    notes: "Erosion Control and Storm Water Management plans must be separate documents.",
    noiLink: "https://dnr.wisconsin.gov/topic/Stormwater/construction/forms",
    agencyContact: { phone: "608-267-7694" }
  },
  wyoming: {
    slug: "wyoming",
    name: "Wyoming",
    abbreviation: "WY",
    authorityName: "Wyoming DEQ",
    permitName: "Large Construction General Permit",
    permitLink: "https://deq.wyoming.gov/water-quality/wmp/storm-water/",
    inspectionFrequency: "Every 14 days + within 24h of 0.25\" rain",
    rainTrigger: "0.25 inches",
    notes: "Self-monitoring inspections are standard.",
    noiLink: "https://deq.wyoming.gov/water-quality/wmp/storm-water/",
    agencyContact: { phone: "307-777-7781" }
  }
};

// Helper to get state or return default generic data
export const getStateData = (slug: string): StateRegulation => {
  const normalized = slug.toLowerCase();
  if (stateRegulations[normalized]) {
    return stateRegulations[normalized];
  }
  
  // Fallback for any missed states, though we aim for 100% coverage
  const formatName = slug.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  return {
    slug: slug,
    name: formatName,
    abbreviation: "US",
    authorityName: "State Environmental Agency",
    permitName: "NPDES General Permit",
    permitLink: "https://www.epa.gov/npdes/stormwater-discharges-construction-activities",
    inspectionFrequency: "Weekly or Bi-Weekly",
    rainTrigger: "0.25 to 0.5 inches",
    notes: "Most states follow the federal EPA guidelines requiring regular inspections and maintenance of BMPs like silt fences and inlet protection.",
    complianceNuance: {
        title: "Standard EPA Alignment",
        description: "This state likely follows the standard EPA Construction General Permit (CGP) baseline requirements.",
        epaComparison: "Same as EPA"
    },
    noiLink: "https://cdx.epa.gov/", // Default to EPA NeT
    agencyContact: {
        phone: "See Local Agency Website"
    }
  };
};

export const getAllStates = () => [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", 
    "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", 
    "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", 
    "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", 
    "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
].map(name => ({ name, slug: name.toLowerCase().replace(/\s+/g, '-') }));
